import React, { useEffect, useRef, useState } from "react";
import { createChart } from "lightweight-charts";
import { getTodayCandles } from "../getTodayCandles";
import DeltaSocketClient from "./DeltaSocket";
import { themeConfig } from "./charthelp";
import { usePrices } from "../../context/PriceContext";

const CandleChart = ({ symbol, onChange, target, sl, show_Lines }) => {
  const pricesData = usePrices(); // ✅ FIXED

  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const targetLineRef = useRef(null);
  const stopLossLineRef = useRef(null);
  const [candleData, setCandleData] = useState([]);
  const socketRef = useRef(null);
  const [theme, setTheme] = useState("light");
  const [timeframe, setTimeframe] = useState("5m");
  const [dayData, setDayData] = useState(7);
  const [targetPrice, setTargetPrice] = useState(target || 0);
  const [stopLossPrice, setStopLossPrice] = useState(sl || 0);
  const [showLines, setShowLines] = useState(false);

  useEffect(() => {
    if (target) {
      setTargetPrice(target);
    }
    if (sl) {
      setStopLossPrice(sl);
    }
  }, [target, sl]);

  // Track current candle being built from ticks
  const currentCandleRef = useRef(null);
  const tickBuffer = useRef([]);
  const lastHistoricalTime = useRef(null);
  const currentCandleStartTime = useRef(null);

  // Timeframe conversion to seconds
  const timeframeToSeconds = {
    "1m": 60,
    "5m": 300,
    "15m": 900,
    "30m": 1800,
    "1h": 3600,
    "4h": 14400,
    "1d": 86400,
  };

  // Auto-calculate target and stop-loss based on current price and OHLC
  const calculateLevels = (currentPrice, ohlcData) => {
    if (!currentPrice || !ohlcData.length) return { target: 0, stopLoss: 0 };

    const latestCandle = ohlcData[ohlcData.length - 1] || {};
    const high = latestCandle.high || currentPrice;
    const low = latestCandle.low || currentPrice;
    const close = latestCandle.close || currentPrice;

    // Calculate ATR-like range for dynamic levels
    const range = high - low;
    const riskRewardRatio = 2; // 1:2 risk-reward

    // Auto target: 2x the range above current price
    const autoTarget = Number(currentPrice) + range * riskRewardRatio;

    // Auto stop-loss: 1x the range below current price
    const autoStopLoss = Number(currentPrice) - range;

    return {
      target: target || autoTarget,
      stopLoss: sl || Math.max(autoStopLoss, low * 0.98), // Don't go below 98% of recent low
    };
  };

  // Create or update target/stop-loss lines
  const updateTradingLines = () => {
    if (!candleSeriesRef.current || !candleData.length) return;

    const currentPrice =
      pricesData[symbol?.toLowerCase()]?.spot_price ||
      candleData[candleData.length - 1]?.close;

    const levels = calculateLevels(currentPrice, candleData);

    if (!showLines) return;

    try {
      // Remove existing lines
      if (targetLineRef.current) {
        candleSeriesRef.current.removePriceLine(targetLineRef.current); // ✅ fixed
        targetLineRef.current = null;
      }
      if (stopLossLineRef.current) {
        candleSeriesRef.current.removePriceLine(stopLossLineRef.current); // ✅ fixed
        stopLossLineRef.current = null;
      }

      // Add new Target line
      if (levels.target > 0) {
        targetLineRef.current = candleSeriesRef.current.createPriceLine({
          price: parseFloat(levels.target), // Number() के बजाय parseFloat()
          color: "#00C851",
          lineWidth: 2,
          lineStyle: 0,
          axisLabelVisible: true,
          title: `Target: ${parseFloat(levels.target)}`,
        });
      }

      // Add new Stop Loss line
      if (levels.stopLoss > 0) {
        stopLossLineRef.current = candleSeriesRef.current.createPriceLine({
          price: levels.stopLoss,
          color: "#FF4444",
          lineWidth: 2,
          lineStyle: 0,
          axisLabelVisible: true,
          title: `SL: ${levels.stopLoss}`,
        });
      }
    } catch (error) {
      console.error("Error updating trading lines:", error);
    }
  };

  // Toggle lines visibility
  const toggleLines = () => {
    setShowLines((prev) => {
      const newState = !prev;

      if (newState) {
        // Add/update lines
        if (candleSeriesRef.current) {
          targetLineRef.current = candleSeriesRef.current.createPriceLine({
            price: targetPrice || 100, // use prop or default
            color: "blue",
            lineWidth: 2,
            lineStyle: 2,
            title: "Target",
          });

          stopLossLineRef.current = candleSeriesRef.current.createPriceLine({
            price: stopLossPrice || 95, // use prop or default
            color: "red",
            lineWidth: 2,
            lineStyle: 2,
            title: "Stop Loss",
          });
        }
      } else {
        // Remove lines
        if (candleSeriesRef.current) {
          if (targetLineRef.current) {
            candleSeriesRef.current.removePriceLine(targetLineRef.current);
            targetLineRef.current = null;
          }
          if (stopLossLineRef.current) {
            candleSeriesRef.current.removePriceLine(stopLossLineRef.current);
            stopLossLineRef.current = null;
          }
        }
      }

      return newState;
    });
  };

  // Detect theme changes
  useEffect(() => {
    const detectTheme = () => {
      const bodyClass = document.body.className;
      const currentTheme = bodyClass.includes("dark") ? "dark" : "light";
      setTheme(currentTheme);
    };

    detectTheme();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          detectTheme();
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  // Helper function to convert UTC timestamp to IST
  const convertToIST = (utcTimestamp) => {
    return utcTimestamp + 5.5 * 60 * 60;
  };

  // Get candle start time for given timestamp and timeframe
  const getCandleStartTime = (timestamp, intervalSeconds) => {
    const ts =
      typeof timestamp === "number"
        ? timestamp
        : Math.floor(new Date(timestamp).getTime() / 1000);
    return Math.floor(ts / intervalSeconds) * intervalSeconds;
  };

  // Normalize timestamp to ensure it's always a Unix timestamp in seconds
  const normalizeTimestamp = (timestamp) => {
    if (typeof timestamp === "number") {
      if (timestamp > 1e15) {
        return Math.floor(timestamp / 1000000);
      } else if (timestamp > 1e12) {
        return Math.floor(timestamp / 1000);
      } else if (timestamp > 1e9) {
        return timestamp;
      }
      return timestamp;
    }

    if (typeof timestamp === "string") {
      return Math.floor(new Date(timestamp).getTime() / 1000);
    }

    if (timestamp instanceof Date) {
      return Math.floor(timestamp.getTime() / 1000);
    }

    if (typeof timestamp === "object" && timestamp.time) {
      return normalizeTimestamp(timestamp.time);
    }

    console.error("Invalid timestamp format:", timestamp);
    return Math.floor(Date.now() / 1000);
  };

  // Create candle from tick data
  const createCandleFromTicks = (ticks, startTime) => {
    if (!ticks.length) return null;

    const prices = ticks.map((tick) => Number(tick.price));
    const normalizedTime = normalizeTimestamp(startTime);

    return {
      time: normalizedTime,
      open: prices[0],
      high: Math.max(...prices),
      low: Math.min(...prices),
      close: prices[prices.length - 1],
    };
  };

  // Update or create candle from tick data
  const updateCandleWithTick = (data, tickTimestamp) => {
    const intervalSeconds = timeframeToSeconds[timeframe];
    const normalizedTickTime = normalizeTimestamp(tickTimestamp);
    const candleStartTime = getCandleStartTime(
      normalizedTickTime,
      intervalSeconds
    );

    // Check if we're starting a new candle
    if (currentCandleStartTime.current !== candleStartTime) {
      // Finalize previous candle if it exists
      if (
        currentCandleRef.current &&
        tickBuffer.current.length > 0 &&
        currentCandleStartTime.current
      ) {
        const finalCandle = createCandleFromTicks(
          tickBuffer.current,
          currentCandleStartTime.current
        );
        if (finalCandle && candleSeriesRef.current) {
          try {
            if (
              !lastHistoricalTime.current ||
              finalCandle.time > lastHistoricalTime.current
            ) {
              candleSeriesRef.current.update(finalCandle);
              lastHistoricalTime.current = finalCandle.time;
            }
          } catch (error) {
            console.error("Error finalizing candle:", error, finalCandle);
          }
        }
      }

      // Start new candle
      currentCandleStartTime.current = candleStartTime;
      tickBuffer.current = [
        {
          price: data.spot_price,
          time: normalizedTickTime,
          open: data.open,
          high: data.high,
          low: data.low,
          close: data.close,
        },
      ];

      const price = Number(data.spot_price);
      currentCandleRef.current = {
        time: candleStartTime,
        open: price,
        high: price,
        low: price,
        close: price,
      };
    } else {
      // Same candle interval - accumulate tick
      tickBuffer.current.push({
        price: data.spot_price,
        time: normalizedTickTime,
        open: data.open,
        high: data.high,
        low: data.low,
        close: data.close,
      });

      if (currentCandleRef.current) {
        const price = Number(data.spot_price);

        if (tickBuffer.current.length === 1) {
          currentCandleRef.current.open = price;
        }

        currentCandleRef.current = {
          ...currentCandleRef.current,
          high: Math.max(currentCandleRef.current.high, price),
          low: Math.min(currentCandleRef.current.low, price),
          close: price,
        };
      }
    }

    if (currentCandleRef.current && candleSeriesRef.current) {
      try {
        if (
          !lastHistoricalTime.current ||
          currentCandleRef.current.time > lastHistoricalTime.current
        ) {
          candleSeriesRef.current.update(currentCandleRef.current);
        }
      } catch (error) {}
    }
  };

  // Fetch historical data
  const fetchCandles = async () => {
    try {
      const data = await getTodayCandles(symbol, timeframe, dayData);

      if (!data?.result) {
        console.warn("No candle data received");
        return;
      }

      const formattedData = data.result
        .map((item) => ({
          time: normalizeTimestamp(convertToIST(item.time)),
          open: Number(item.open),
          high: Number(item.high),
          low: Number(item.low),
          close: Number(item.close),
          volume: Number(item.volume),
        }))
        .sort((a, b) => a.time - b.time);

      setCandleData(formattedData);

      if (formattedData.length > 0) {
        lastHistoricalTime.current =
          formattedData[formattedData.length - 1].time;
      }
    } catch (err) {
      console.error("Error fetching candles:", err);
    }
  };

  const applyThemeToChart = (chart, currentTheme) => {
    const config = themeConfig[currentTheme];

    chart.applyOptions({
      layout: {
        background: { color: config.backgroundColor },
        textColor: config.textColor,
      },
      grid: {
        vertLines: { color: config.gridColor },
        horzLines: { color: config.gridColor },
      },
      crosshair: {
        mode: 0,
        vertLine: {
          color: config.crosshairColor,
          width: 1,
          style: 2,
          visible: true,
          labelVisible: true,
        },
        horzLine: {
          color: config.crosshairColor,
          width: 1,
          style: 2,
          visible: true,
          labelVisible: true,
        },
      },
      rightPriceScale: {
        borderColor: config.borderColor,
        textColor: config.textColor,
      },
      timeScale: {
        borderColor: config.borderColor,
        textColor: config.textColor,
      },
    });

    if (candleSeriesRef.current) {
      candleSeriesRef.current.applyOptions({
        upColor: config.upColor,
        downColor: config.downColor,
        borderUpColor: config.borderUpColor,
        borderDownColor: config.borderDownColor,
        wickUpColor: config.wickUpColor,
        wickDownColor: config.wickDownColor,
      });
    }
  };

  useEffect(() => {
    if (!candleData.length) return;

    const config = themeConfig[theme];

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: window.innerWidth > 1000 ? 500 : 300,
      layout: {
        background: { color: config.backgroundColor },
        textColor: config.textColor,
        fontSize: 12,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      },
      grid: {
        vertLines: {
          color: config.gridColor,
          style: 2,
          visible: true,
        },
        horzLines: {
          color: config.gridColor,
          style: 2,
          visible: true,
        },
      },
      crosshair: {
        mode: 0,
        vertLine: {
          color: config.crosshairColor,
          width: 1,
          style: 2,
          visible: true,
          labelVisible: true,
        },
        horzLine: {
          color: config.crosshairColor,
          width: 1,
          style: 2,
          visible: true,
          labelVisible: true,
        },
      },
      rightPriceScale: {
        borderColor: config.borderColor,
        textColor: config.textColor,
        visible: true,
        entireTextOnly: true,
      },
      timeScale: {
        borderColor: config.borderColor,
        textColor: config.textColor,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 12,
        barSpacing: 8,
        minBarSpacing: 4,
        fixLeftEdge: false,
        fixRightEdge: false,
        lockVisibleTimeRangeOnResize: true,
        timeFormatter: (timestamp) => {
          const date = new Date(timestamp * 1000);
          return date.toLocaleString("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: "Asia/Kolkata",
          });
        },
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: config.upColor,
      downColor: config.downColor,
      borderUpColor: config.borderUpColor,
      borderDownColor: config.borderDownColor,
      wickUpColor: config.wickUpColor,
      wickDownColor: config.wickDownColor,
      priceFormat: {
        type: "price",
        precision: 6,
        minMove: 0.000001,
      },
    });

    candleSeries.setData(candleData);
    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    setTimeout(() => {
      updateTradingLines();
    }, 100);

    const handleResize = () => {
      if (chart && chartContainerRef.current) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.remove();
    };
  }, [candleData, theme]);

  useEffect(() => {
    if (chartRef.current) {
      applyThemeToChart(chartRef.current, theme);
      // Re-add lines after theme change
      setTimeout(() => {
        updateTradingLines();
      }, 100);
    }
  }, [theme]);

  // Update trading lines when data changes
  useEffect(() => {
    if (candleData.length > 0 && candleSeriesRef.current) {
      setTimeout(() => {
        updateTradingLines();
      }, 200);
    }
  }, [candleData, showLines]);

  // 🔹 Step 2: real-time updates handle करो
  const handleUpdate = (data) => {
    const tickTime = data.timestamp || data.time || Date.now();
    const normalizedTime = normalizeTimestamp(tickTime);
    const istTime = convertToIST(normalizedTime);

    updateCandleWithTick(data, istTime);

    onChange && onChange(data.spot_price);
  };
  
  useEffect(() => {
    const cached = pricesData[symbol];
    if (cached) {
      handleUpdate(cached);
    }
  }, [symbol, timeframe,pricesData]);

  useEffect(() => {
    const intervalSeconds = timeframeToSeconds[timeframe];
    const checkInterval = 1000;

    const interval = setInterval(() => {
      const now = Math.floor(Date.now() / 1000);
      const istNow = convertToIST(now);
      const currentCandleStart = getCandleStartTime(istNow, intervalSeconds);

      if (
        currentCandleStartTime.current &&
        currentCandleStartTime.current < currentCandleStart &&
        tickBuffer.current.length > 0 &&
        currentCandleRef.current
      ) {
        const finalCandle = createCandleFromTicks(
          tickBuffer.current,
          currentCandleStartTime.current
        );
        if (finalCandle && candleSeriesRef.current) {
          try {
            if (
              !lastHistoricalTime.current ||
              finalCandle.time > lastHistoricalTime.current
            ) {
              candleSeriesRef.current.update(finalCandle);
              lastHistoricalTime.current = finalCandle.time;
            }
          } catch (error) {}
        }

        tickBuffer.current = [];
        currentCandleRef.current = null;
        currentCandleStartTime.current = null;
      }
    }, checkInterval);

    return () => clearInterval(interval);
  }, [timeframe]);

  useEffect(() => {
    if (symbol) {
      currentCandleRef.current = null;
      tickBuffer.current = [];
      currentCandleStartTime.current = null;
      lastHistoricalTime.current = null;

      fetchCandles();
    }
  }, [symbol, timeframe, dayData]);

  const config = themeConfig[theme];

  return (
    <div
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        backgroundColor: config.backgroundColor,
        border: `1px solid ${config.borderColor}`,
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {/* Enhanced Header with Trading Controls */}
      <div
        style={{
          padding: "12px 10px",
          backgroundColor: config.headerBg,
          borderBottom: `1px solid ${config.headerBorder}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <div>
            <div
              style={{
                fontSize: "15px",
                fontWeight: "600",
                color: config.headerText,
                marginBottom: "4px",
              }}
            >
              {symbol}
              <span
                // className={}
                style={{
                  marginLeft: "12px",
                  color:
                    pricesData[symbol?.toUpperCase()]?.mark_change_24h >= 0
                      ? config.upColor
                      : config.downColor,
                  fontWeight: "500",
                }}
              >
                {pricesData[symbol?.toUpperCase()]?.spot_price || "Loading..."}
              </span>
            </div>
            <div
              className="d-none d-md-block"
              style={{
                fontSize: "13px",
                color: config.textColor,
              }}
            >
              {timeframe} • Real-time updates
            </div>
          </div>
          <div
            style={{
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            <span
              className={`mark_change_24h_${symbol?.toUpperCase()}`}
              style={{
                color:
                  pricesData[symbol?.toUpperCase()]?.mark_change_24h >= 0
                    ? config.upColor
                    : config.downColor,
                backgroundColor:
                  pricesData[symbol?.toUpperCase()]?.mark_change_24h >= 0
                    ? `${config.upColor}15`
                    : `${config.downColor}15`,
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "12px",
              }}
            >
              {pricesData[symbol?.toUpperCase()]?.mark_change_24h >= 0
                ? "+"
                : ""}
              {pricesData[symbol?.toUpperCase()]?.mark_change_24h}%
            </span>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "0",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Trading Lines Controls */}
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {show_Lines && (
              <button
                onClick={toggleLines}
                style={{
                  padding: "2px 4px",
                  borderRadius: "4px",
                  border: `1px solid ${config.borderColor}`,
                  backgroundColor: showLines
                    ? config.upColor
                    : config.backgroundColor,
                  color: showLines ? "white" : config.textColor,
                  fontSize: "12px",
                  cursor: "pointer",
                  fontWeight: "500",
                }}
              >
                {showLines ? "Hide Lines" : "Show Lines"}
              </button>
            )}
          </div>

          {/* Timeframe SELECT */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginRight: "5px",
              fontSize: "12px",
              color: config.textColor,
              opacity: 0.7,
            }}
          >
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              style={{
                padding: "2px 4px",
                borderRadius: "4px",
                border: `1px solid ${config.borderColor}`,
                backgroundColor: config.backgroundColor,
                color: config.textColor,
                fontSize: "11px",
              }}
            >
              <option value="1m">1 Minute</option>
              <option value="5m">5 Minutes</option>
              <option value="15m">15 Minutes</option>
              <option value="30m">30 Minutes</option>
              <option value="1h">1 Hour</option>
              <option value="4h">4 Hours</option>
              <option value="1d">1 Day</option>
            </select>
          </div>

          {/* Data Get Days */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1px",
              fontSize: "12px",
              color: config.textColor,
              opacity: 0.7,
            }}
          >
            <select
              value={dayData}
              onChange={(e) => setDayData(Number(e.target.value))}
              style={{
                padding: "2px 4px",
                borderRadius: "4px",
                border: `1px solid ${config.borderColor}`,
                backgroundColor: config.backgroundColor,
                color: config.textColor,
                fontSize: "11px",
              }}
            >
              <option value={7}>7 Days</option>
              <option value={14}>14 Days</option>
              <option value={30}>30 Days</option>
              <option value={60}>60 Days</option>
              <option value={90}>90 Days</option>
              <option value={180}>180 Days</option>
              <option value={365}>365 Days</option>
            </select>
          </div>
        </div>
      </div>

      <div
        // className="chartmedia"
        ref={chartContainerRef}
        style={{
          width: "100%",
          height: window.innerWidth > 1000 ? 500 : 300,
          backgroundColor: config.backgroundColor,
        }}
      />
    </div>
  );
};

export default CandleChart;
